import Download from '@mui/icons-material/Download';
import Link from '@mui/icons-material/Link';
import Mail from '@mui/icons-material/Mail';
import Share from '@mui/icons-material/Share';
import ShareOutlined from '@mui/icons-material/ShareOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { createContext, type ReactNode, useContext, useState } from 'react';

import { Button } from '../Button/Button';

const SHARE_MODAL_DESCRIPTION_ID = 'share-modal-description';
const SHARE_MODAL_TITLE_ID = 'share-modal-title';

type ShareModalLabels = {
  closeButton: string;
  copiedLink: string;
  copyLink: string;
  downloadPdf: string;
  email: string;
  generatingPdf: string;
  openButton: string;
  openIconButtonAriaLabel: string;
  whatsapp: string;
};

type ShareModalContextType = {
  url: string;
  subject: string;
  content: string;
  labels: ShareModalLabels;
  onShare?: (method: string) => void;
};

const ShareModalContext = createContext<ShareModalContextType | null>(null);

const useShareModalContext = () => {
  const context = useContext(ShareModalContext);
  if (!context) {
    throw new Error('Share modal components must be used within ShareModal');
  }
  return context;
};

export const ShareModal = ({
  children,
  labels,
  title,
  url,
  subject,
  content,
  onShare,
  onClick,
  slotProps,
  'data-testid': dataTestId,
}: {
  children: ReactNode;
  labels: ShareModalLabels;
  title: string;
  url: string;
  subject: string;
  content: string;
  onShare?: (method: string) => void;
  onClick?: () => void;
  slotProps?: {
    button: {
      variant?: 'primary' | 'secondary' | 'icon';
      size?: 'small' | 'medium';
    };
  };
  'data-testid'?: string;
}) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      {slotProps?.button.variant === 'icon' ? (
        <IconButton
          onClick={() => {
            handleOpen();
            onClick?.();
          }}
          aria-label={labels.openIconButtonAriaLabel}
          size={slotProps?.button.size ?? 'medium'}
          data-testid={dataTestId}
        >
          <ShareOutlined />
        </IconButton>
      ) : (
        <Button
          variant={slotProps?.button.variant ?? 'primary'}
          fullWidth
          onClick={() => {
            handleOpen();
            onClick?.();
          }}
          size={slotProps?.button.size ?? 'medium'}
          startIcon={<Share />}
          data-testid={dataTestId}
        >
          {labels.openButton}
        </Button>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby={SHARE_MODAL_TITLE_ID}
        aria-describedby={SHARE_MODAL_DESCRIPTION_ID}
        maxWidth="md"
      >
        <DialogTitle id={SHARE_MODAL_TITLE_ID}>{title}</DialogTitle>
        <DialogContent id={SHARE_MODAL_DESCRIPTION_ID}>
          <ShareModalContext.Provider value={{ content, labels, onShare, subject, url }}>
            <Stack
              spacing={2.5}
              sx={{
                minWidth: '200px',
              }}
            >
              {children}
            </Stack>
          </ShareModalContext.Provider>
        </DialogContent>

        <DialogActions>
          <Button fullWidth variant="secondary" onClick={handleClose}>
            {labels.closeButton}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const CopyLink = ({ onClick }: { onClick?: () => void }) => {
  const { url, labels, onShare } = useShareModalContext();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    onShare?.('link');
    onClick?.();
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      startIcon={<Link color="action" />}
      onClick={handleCopy}
      variant="text"
      sx={{ justifyContent: 'flex-start' }}
    >
      {copied ? labels.copiedLink : labels.copyLink}
    </Button>
  );
};

ShareModal.CopyLink = CopyLink;

const Email = ({ onClick }: { onClick?: () => void }) => {
  const { subject, content, labels, onShare } = useShareModalContext();

  const handleClick = () => {
    onShare?.('email');
    onClick?.();
  };

  return (
    <Button
      startIcon={<Mail color="action" />}
      variant="text"
      component="a"
      href={`mailto:?subject=${subject}&body=${encodeURIComponent(content)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      sx={{ justifyContent: 'flex-start' }}
    >
      {labels.email}
    </Button>
  );
};

ShareModal.Email = Email;

const WhatsApp = ({ onClick }: { onClick?: () => void }) => {
  const { content, labels, onShare } = useShareModalContext();

  const handleClick = () => {
    onShare?.('whatsapp');
    onClick?.();
  };

  return (
    <Button
      startIcon={<WhatsAppIcon color="action" />}
      variant="text"
      component="a"
      href={`https://wa.me/?text=${encodeURIComponent(content)}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      sx={{ justifyContent: 'flex-start' }}
    >
      {labels.whatsapp}
    </Button>
  );
};

ShareModal.WhatsApp = WhatsApp;

type Props = {
  onClick?: () => void;
  isGenerating: boolean;
};
const DownloadPDF = ({ onClick, isGenerating }: Props) => {
  const { labels, onShare } = useShareModalContext();

  const handleClick = () => {
    onShare?.('download');
    onClick?.();
  };

  return (
    <Button
      startIcon={<Download color="action" />}
      variant="text"
      onClick={handleClick}
      sx={{ justifyContent: 'flex-start' }}
      disabled={isGenerating}
    >
      {isGenerating ? labels.generatingPdf : labels.downloadPdf}
    </Button>
  );
};

ShareModal.DownloadPDF = DownloadPDF;
